import { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import type { ChartConfiguration, ChartDataset, ChartType } from 'chart.js';
import { THEME_EVENT } from '../theme';

// Read a CSS design token at runtime so charts follow the active light/dark theme.
function cssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function cssPx(name: string, fallback: number): number {
  const parsed = Number.parseFloat(cssVar(name, `${fallback}px`));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function cssWeight(name: string, fallback: number): number {
  const parsed = Number.parseInt(cssVar(name, String(fallback)), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

// Tick/grid colors inherit from Chart.js globals so even charts that override
// `scales` (doughnut, dual-axis) still pick up the themed axis colors.
function applyChartTheme(): void {
  Chart.defaults.color = cssVar('--chart-tick', '#6b7280');
  Chart.defaults.borderColor = cssVar('--chart-grid', '#eef1f5');
}
applyChartTheme();

// Always-on data labels (numbers on charts, no hover needed). Registered globally so every
// ChartBox gets them; set as a Chart default so per-chart `options.plugins` overrides don't drop it.
// Smart defaults: bars only (not line/pie points), hide zeros, color follows the theme token.
Chart.register(ChartDataLabels);
Chart.defaults.set('plugins.datalabels', {
  display: (ctx: {
    dataset: { type?: string; data: unknown[] };
    chart: { config: { type?: string }; width: number; height: number; options: { indexAxis?: string } };
    dataIndex: number;
  }) => {
    const t = ctx.dataset.type || ctx.chart.config.type;
    if (t !== 'bar') return false;
    const v = ctx.dataset.data[ctx.dataIndex];
    if (typeof v !== 'number' || v <= 0) return false;
    // density guard: only show when there's room per bar (prevents overlap on dense/narrow charts)
    const n = ctx.dataset.data.length || 1;
    const horizontal = ctx.chart.options.indexAxis === 'y';
    const room = horizontal ? ctx.chart.height / n : ctx.chart.width / n;
    return room > 18;
  },
  anchor: 'end',
  align: 'end',
  clamp: true,
  color: () => cssVar('--chart-label', '#334155'), // scriptable → recomputed each draw, follows theme
  font: () => ({ size: cssPx('--text-xs', 11), weight: cssWeight('--fw-semibold', 600) }),
  formatter: (v: unknown) => (typeof v === 'number' ? v.toLocaleString('vi-VN') : v),
});

export function chartDataset(
  label: string,
  data: number[],
  color: string,
  type: ChartType = 'bar',
  yAxisID = 'y',
): ChartDataset {
  return {
    label,
    data,
    type,
    yAxisID,
    borderColor: color,
    backgroundColor: `${color}55`,
    borderWidth: 2,
    tension: 0.35,
    pointRadius: 2,
    borderRadius: 5,
  } as ChartDataset;
}

interface ChartBoxProps {
  type?: ChartType;
  labels: string[];
  datasets: ChartDataset[];
  options?: ChartConfiguration['options'];
  onPointClick?: (payload: { index: number; datasetIndex: number; label: string }) => void;
}

export function ChartBox({ type = 'bar', labels, datasets, options, onPointClick }: ChartBoxProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const [themeTick, setThemeTick] = useState(0);

  // Repaint with fresh themed colors when the user toggles light/dark.
  useEffect(() => {
    const onTheme = () => { applyChartTheme(); setThemeTick(value => value + 1); };
    window.addEventListener(THEME_EVENT, onTheme);
    return () => window.removeEventListener(THEME_EVENT, onTheme);
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return undefined;
    chartRef.current?.destroy();
    const gridColor = cssVar('--chart-grid', '#eef1f5');
    const tickFont = { size: cssPx('--text-xs', 11), weight: cssWeight('--fw-medium', 500) };
    const labelFont = { size: cssPx('--text-sm', 12), weight: cssWeight('--fw-medium', 500) };
    chartRef.current = new Chart(canvasRef.current, {
      type,
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: labelFont } }, tooltip: { padding: 10 } },
        scales: {
          x: { grid: { display: false }, ticks: { font: tickFont } },
          y: { beginAtZero: true, ticks: { font: tickFont }, grid: { color: gridColor } },
        },
        // Click must land ON a bar/point (intersect:true) — the global interaction mode is
        // index/intersect:false (good for hover tooltips) but would misfire clicks on empty space.
        onClick: onPointClick
          ? (event, _elements, chart) => {
              const native = (event as { native?: Event | null }).native || (event as unknown as Event);
              const hits = chart.getElementsAtEventForMode(native, 'nearest', { intersect: true }, true);
              const hit = hits[0];
              if (hit) onPointClick({ index: hit.index, datasetIndex: hit.datasetIndex, label: String(chart.data.labels?.[hit.index] || '') });
            }
          : undefined,
        onHover: onPointClick
          ? (event, _els, chart) => {
              const native = (event as { native?: Event | null }).native || (event as unknown as Event);
              const hits = chart.getElementsAtEventForMode(native, 'nearest', { intersect: true }, true);
              (chart.canvas as HTMLCanvasElement).style.cursor = hits.length ? 'pointer' : 'default';
            }
          : undefined,
        ...options,
      },
    });
    return () => chartRef.current?.destroy();
  }, [type, themeTick, JSON.stringify(labels), JSON.stringify(datasets), JSON.stringify(options)]);

  return <canvas ref={canvasRef} />;
}
